import * as React from "react";
import { Upload, RefreshCw } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/Select";
import { useSpaceStore } from "../../stores/spaceStore";
import { updateSpace, getVendors } from "../../services/api";
import type { Vendor } from "../../services/api";
import { useToast } from "../ui/Toast";

export function DeploySection() {
  const [spaceName, setSpaceName] = React.useState("");
  const [visitorId, setVisitorId] = React.useState("");
  // Who sees the space in the visualizer. Maps onto the two backend fields:
  //   hidden -> uploadedByVisitor (no library shows it), vendor kept as is
  //   all    -> public, no vendor: every vendor's library
  //   vendor -> public + associatedVendor: that vendor's library only
  const [visibility, setVisibility] = React.useState<"hidden" | "all" | "vendor">("hidden");
  const [associatedVendor, setAssociatedVendor] = React.useState("");
  const [vendors, setVendors] = React.useState<Vendor[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [statusOutput, setStatusOutput] = React.useState<string[]>([]);

  const { spaceId, spaceInfo } = useSpaceStore();
  const { addToast } = useToast();

  // Initialize form with space info
  React.useEffect(() => {
    if (spaceInfo) {
      setSpaceName(spaceInfo.name || "");
      setVisitorId(spaceInfo.visitor || "");
      setAssociatedVendor(spaceInfo.associatedVendor || "");
      // Start from what the space IS: a checkbox that always started unticked
      // quietly hid a public space whenever someone pressed Update.
      setVisibility(
        spaceInfo.uploadedByVisitor !== false ? "hidden" : spaceInfo.associatedVendor ? "vendor" : "all"
      );
    }
  }, [spaceInfo]);

  const isPublic = visibility !== "hidden";
  const vendorToSend = visibility === "all" ? "" : associatedVendor;

  // Load vendors on mount
  React.useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    setIsLoadingVendors(true);
    try {
      const response = await getVendors();
      if (response.status === "SUCCESS") {
        setVendors(response.vendors);
      }
    } catch (error) {
      console.error("Failed to load vendors:", error);
    } finally {
      setIsLoadingVendors(false);
    }
  };

  const addStatus = (message: string) => {
    setStatusOutput((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const handleUpdateSpace = async () => {
    if (!spaceId) {
      addToast("error", "No space loaded");
      return;
    }
    if (visibility === "vendor" && !associatedVendor) {
      addToast("error", "Choose the vendor this space is for");
      return;
    }

    setIsUpdating(true);
    setStatusOutput([]);
    addStatus("Starting space update...");

    try {
      addStatus(`Updating space: ${spaceId}`);
      addStatus(`Name: ${spaceName || "(unchanged)"}`);
      addStatus(`Visitor ID: ${visitorId || "(none)"}`);
      addStatus(`Public: ${isPublic ? "Yes" : "No"}`);
      addStatus(`Associated Vendor: ${vendorToSend || "(none)"}`);
      addStatus("");
      addStatus("Uploading files to S3...");

      const response = await updateSpace(visitorId, spaceName, vendorToSend, isPublic, spaceId);

      if (response.status === "SUCCESS") {
        addStatus("");
        addStatus("Update completed successfully!");

        if (response.updated_files && response.updated_files.length > 0) {
          addStatus(`Updated files: ${response.updated_files.join(", ")}`);
        }

        if (response.urls) {
          addStatus("");
          addStatus("Updated URLs:");
          for (const [key, value] of Object.entries(response.urls)) {
            addStatus(`  ${key}: ${value}`);
          }
        }

        // Show cache status
        addStatus("");
        if (response.cache_cleared) {
          addStatus("Cache cleared successfully");
        } else {
          addStatus("Warning: Cache was not cleared - you may need to wait for CDN cache to expire");
        }

        addToast("success", "Space updated successfully");
      } else {
        throw new Error(response.message || "Failed to update space");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update space";
      addStatus("");
      addStatus(`ERROR: ${message}`);
      addToast("error", message);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!spaceId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Load a space first to deploy updates
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Upload className="h-5 w-5" />
          Deploy / Update Space
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Compact single-row form */}
        <div className="flex items-end gap-3 flex-wrap">
          {/* Space Name */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input
              placeholder="Space name..."
              value={spaceName}
              onChange={(e) => setSpaceName(e.target.value)}
              className="w-40 h-9"
            />
          </div>

          {/* Visitor ID */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Visitor ID</label>
            <Input
              placeholder="Optional..."
              value={visitorId}
              onChange={(e) => setVisitorId(e.target.value)}
              className="w-32 h-9"
            />
          </div>

          {/* Visibility */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Visible to</label>
            <Select value={visibility} onValueChange={(value) => setVisibility(value as typeof visibility)}>
              <SelectTrigger className="w-40 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hidden">Nobody (draft)</SelectItem>
                <SelectItem value="all">All vendors</SelectItem>
                <SelectItem value="vendor">One vendor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Associated Vendor */}
          {visibility === "vendor" && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Vendor</label>
            <div className="flex gap-1">
              <Select
                value={associatedVendor || "none"}
                onValueChange={(value) => setAssociatedVendor(value === "none" ? "" : value)}
                disabled={isLoadingVendors}
              >
                <SelectTrigger className="w-36 h-9">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadVendors}
                disabled={isLoadingVendors}
                className="px-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoadingVendors ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
          )}

          {/* Update Button */}
          <Button
            onClick={handleUpdateSpace}
            loading={isUpdating}
            size="sm"
            className="ml-auto"
          >
            <Upload className="h-4 w-4 mr-1" />
            Update
          </Button>
        </div>

        {/* Status Output - more compact */}
        {statusOutput.length > 0 && (
          <div className="bg-muted rounded p-3 font-mono text-xs max-h-40 overflow-y-auto border">
            {statusOutput.map((line, index) => (
              <div
                key={index}
                className={`${
                  line.includes("ERROR")
                    ? "text-destructive"
                    : line.includes("SUCCESS") || line.includes("completed")
                    ? "text-green-600 dark:text-green-400"
                    : ""
                }`}
              >
                {line || "\u00A0"}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
